import React, { useEffect, useState, useMemo } from 'react'
import { Tooltip, IconButton } from '@mui/material'
import { useHistory } from 'react-router'
import {
    UploadIcon,
    Icon,
    CaseBuilder,
    CaseBuilderIssuer,
    DocumentMagnifyingGlassIcon,
} from 'components'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import { useUiStateContext } from 'context/UiState/UiStateContext'
import { useSWRContext } from 'context/SWRContext'
import { CaseStatusId } from 'hooks/cb-api'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { alertSnackbarContentProps } from 'components/AlertSnackbar'
import { ConfirmationModal } from 'views/CaseQCReview/CaseReviewComponents'
// import CB from 'lib'

interface DnrActionIconProps {
    row: any
    iconSize?: 'small' | 'medium' | 'large' | undefined
    rehydrateView: () => void
    returnNetworkStatus?: (status: boolean) => void
    caseModel?: any
    setShowSnackbar?: (status: boolean) => void
    testId: string
    selectedTabFilter?: string
    setAlertSnackbarMainProps: (value: alertSnackbarContentProps) => void
    setAlertSnackbarMainOpen: (status: boolean) => void
    showClientTypeIssuerUI: boolean
    showDNRAction?: boolean
    showDNRActionOnly?: boolean
    selectedTileType?: string
}

interface DnrProps {
    openModal: boolean
    toggleModal: () => void
    id: number
    rehydrateView: () => void
    callAction: (case_id: number, status_id: CaseStatusId) => any
    loading: boolean
    setAlertSnackbarMainProps: (value: alertSnackbarContentProps) => void
    setAlertSnackbarMainOpen: (status: boolean) => void
    showClientTypeIssuerUI: boolean
}

const ArchiveDraftModal = ({
    openModal,
    toggleModal,
    id,
    // rehydrateView,
    callAction,
    // loading,
    setAlertSnackbarMainProps,
    setAlertSnackbarMainOpen,
}: DnrProps): React.ReactElement => {
    const revertCaseToNew = () =>
        callAction(id, CaseStatusId.New)
            .then(() => {
                setAlertSnackbarMainProps({
                    message: 'Your action was a success!',
                    title: 'Success.',
                    intent: 'success',
                })
            })
            .catch(() => {
                setAlertSnackbarMainProps({
                    message: 'There was an error, try again.',
                    title: 'Error.',
                    intent: 'error',
                })
            })
            .finally(() => {
                toggleModal()
                setAlertSnackbarMainOpen(true)
            })

    return (
        <div>
            <ConfirmationModal
                open={openModal}
                title={'Delete Case'}
                message={
                    'You are about to delete the case. Would you like to proceed?'
                }
                primaryButton={{
                    text: 'Delete',
                    onClick: revertCaseToNew,
                }}
                secondaryButton={{ text: 'Cancel', onClick: toggleModal }}
            />
        </div>
    )
}

const RevertDnr = ({
    openModal,
    toggleModal,
    id,
    // rehydrateView,
    callAction,
    loading,
    setAlertSnackbarMainProps,
    setAlertSnackbarMainOpen,
    showClientTypeIssuerUI,
}: DnrProps): React.ReactElement => {
    const revertCaseToNew = () =>
        callAction(id, CaseStatusId.New)
            .then(() => {
                setAlertSnackbarMainProps({
                    message: 'Your action was a success!',
                    title: 'Success.',
                    intent: 'success',
                })
            })
            .catch(() => {
                setAlertSnackbarMainProps({
                    message: 'There was an error, try again.',
                    title: 'Error.',
                    intent: 'error',
                })
            })
            .finally(() => {
                toggleModal()
                setAlertSnackbarMainOpen(true)
            })

    return (
        <div>
            <ConfirmationModal
                open={openModal}
                title={
                    showClientTypeIssuerUI
                        ? 'Unarchive Case'
                        : 'Revert Do Not Represent'
                }
                message={
                    showClientTypeIssuerUI
                        ? 'You are about to unarchive the cases. Would you like to proceed?'
                        : 'You are about to revert case(s)'
                }
                primaryButton={{
                    text: 'Unarchive',
                    onClick: revertCaseToNew,
                }}
                secondaryButton={{ text: 'Cancel', onClick: toggleModal }}
            />
        </div>
    )
}

const DoNotRepresent = ({
    openModal,
    toggleModal,
    id,
    // rehydrateView,
    callAction,
    // loading,
    setAlertSnackbarMainProps,
    setAlertSnackbarMainOpen,
    showClientTypeIssuerUI,
}: DnrProps): React.ReactElement => {
    const moveToDnrStatus = () =>
        callAction(id, CaseStatusId.DoNotRepresent)
            .then(() => {
                setAlertSnackbarMainProps({
                    message: 'Your action was a success!',
                    title: 'Success.',
                    intent: 'success',
                })
            })
            .catch(() => {
                setAlertSnackbarMainProps({
                    message: 'There was an error, try again.',
                    title: 'Error.',
                    intent: 'error',
                })
            })
            .finally(() => {
                toggleModal()
                setAlertSnackbarMainOpen(true)
            })

    return (
        <div>
            <ConfirmationModal
                open={openModal}
                title={
                    showClientTypeIssuerUI
                        ? 'Archive Case'
                        : 'Move Case to Do Not Represent'
                }
                message={
                    showClientTypeIssuerUI
                        ? 'You are about to archive the case. Would you like to proceed?'
                        : 'You are about to move this case to do not represent and mark as a loss, would you like to proceed?'
                }
                primaryButton={{
                    text: 'Archive',
                    onClick: moveToDnrStatus,
                }}
                secondaryButton={{ text: 'Cancel', onClick: toggleModal }}
            />
        </div>
    )
}

/**
 * Use DnrActionIcon to ___________________
 */
const DnrActionIcon = ({
    row,
    rehydrateView,
    testId,
    selectedTabFilter = '',
    setAlertSnackbarMainProps,
    setAlertSnackbarMainOpen,
    showClientTypeIssuerUI,
    showDNRAction = true,
    showDNRActionOnly = false,
    selectedTileType,
}: DnrActionIconProps): React.ReactElement => {
    const history = useHistory()
    const { clientTheme } = useThemeStyling()
    const uiState = useUiStateContext()
    const { user } = useAuthedUser()
    const isReadOnly = user?.is_read_only

    const [openMoveToDnr, setOpenMoveToDnr] = useState(false)
    const [openRevertDnr, setOpenRevertDnr] = useState(false)
    const [openArchiveDraft, setOpenArchiveDraft] = useState(false)
    const [statusState, setStatusState] = useState({
        tooltip: '',
        action: () => {},
        icon: '',
    })
    const [openCaseBuilder, setOpenCaseBuilder] = useState<boolean>(false)

    const showCaseBuilderIcon = (uiState.whoami?.roles ?? []).some(
        (role) => role === 'case-builder'
    )

    const memoRow = useMemo(() => row, [row])

    const enableCaseBuilderIcon = (() => {
        // Standard UI mode conditions
        if (!showClientTypeIssuerUI) {
            const isAssignedToCurrentUser = Boolean(
                row.original?.assigned_user?.id === uiState.whoami?.id
            )
            const isNewCasesTab = selectedTabFilter === 'new_cases'
            return isAssignedToCurrentUser || isNewCasesTab
        }

        // Issuer UI mode conditions
        if (showClientTypeIssuerUI) {
            const isSaveForLater =
                memoRow.original?.case_note?.toLocaleLowerCase() ===
                'save for later'
            const isRejectionPendingTab = selectedTabFilter === 'expired'
            return isSaveForLater || isRejectionPendingTab
        }

        return false
    })()

    const { swrActions } = useSWRContext()

    const { casesLoading, caseNetworkActions } = swrActions

    const {
        shouldDisableRevertDNR,
        shouldDisableDNR,
        shouldDisableUpload,
    } = swrActions.caseViewActions

    const { setCaseStatus } = caseNetworkActions

    const toggleMoveToDnr = (): void => setOpenMoveToDnr((prev) => !prev)

    const toggleRevertDnr = (): void => setOpenRevertDnr((prev) => !prev)

    const toggleArchiveDraft = (): void => setOpenArchiveDraft((prev) => !prev)

    useEffect(() => {
        if (
            showClientTypeIssuerUI &&
            row.original?.case_note?.toLocaleLowerCase() === 'save for later' &&
            selectedTileType !== 'archived'
        ) {
            return setStatusState({
                tooltip: 'Archive Case',
                action: toggleMoveToDnr,
                icon: 'icon-Dnr',
            })
        }

        if (showClientTypeIssuerUI && selectedTileType === 'archived') {
            return setStatusState({
                tooltip: showClientTypeIssuerUI
                    ? 'Unarchive Case'
                    : 'Revert Do Not Represent',
                action: toggleRevertDnr,
                icon: 'icon-revert-Dnr',
            })
        }

        if (
            !shouldDisableRevertDNR(
                row.original.properties?.is_revert_dnr_enabled
            )
        ) {
            return setStatusState({
                tooltip: showClientTypeIssuerUI
                    ? 'Unarchive Case'
                    : 'Revert Do Not Represent',
                action: toggleRevertDnr,
                icon: 'icon-revert-Dnr',
            })
        }
        if (shouldDisableDNR(row.original.properties?.is_dnr_enabled)) {
            return setStatusState({
                tooltip: showClientTypeIssuerUI
                    ? 'Revert Draft'
                    : `Case ${row.original.status_group}`,
                action: () => {},
                icon: 'icon-revert-Dnr-disabled',
            })
        }
        return setStatusState({
            tooltip: 'Do Not Represent',
            action: toggleMoveToDnr,
            icon: 'icon-Dnr',
        })
        // each time a network call is made, isLoading toggles and will allow for this useEffect to re-run the statement after cache flush
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memoRow])

    const handleClickReview = () => {
        history.push({
            pathname: `/case-qc-review/view-case/${memoRow.original.id}`,
            state: {
                caseId: memoRow.original.id,
                reviewType: 'qc-review',
            },
        })
    }
    const showArchiveIcon = (() => {
        // Merchant clients
        if (!showClientTypeIssuerUI) {
            return false
        }
        // Issuer clients
        if (showClientTypeIssuerUI) {
            const allowedTabsIssuer = ['not_disputed'].includes(
                selectedTabFilter
            )
            return allowedTabsIssuer
        }
        return false
    })()

    const enableArchiveIcon = (() => {
        // Merchant clients
        if (!showClientTypeIssuerUI) {
            return false
        }
        // Issuer clients
        if (showClientTypeIssuerUI) {
            const enabledArchiveIssuer =
                row.original?.case_note?.toLocaleLowerCase() ===
                    'save for later' &&
                ['not_disputed'].includes(selectedTabFilter)
            return enabledArchiveIssuer
        }

        return false
    })()

    return (
        <div>
            {showDNRActionOnly ? (
                <Tooltip arrow placement="top" title={statusState.tooltip}>
                    <div>
                        <IconButton
                            onClick={statusState.action}
                            className={'emp-dnrActionIcon-iconBtn'}
                            disabled={
                                isReadOnly ||
                                (!showClientTypeIssuerUI &&
                                    shouldDisableRevertDNR(
                                        row.original.properties
                                            ?.is_revert_dnr_enabled
                                    ) &&
                                    shouldDisableDNR(
                                        row.original.properties?.is_dnr_enabled
                                    )) ||
                                !(
                                    showClientTypeIssuerUI &&
                                    row.original?.case_note?.toLocaleLowerCase() ===
                                        'save for later'
                                )
                            }
                        >
                            <Icon
                                className={`${statusState.icon} ${
                                    isReadOnly &&
                                    'emp-dnrActionIcon-disabledIcon'
                                }`}
                                id={`${testId}-dnr-icon-${memoRow.id}`}
                                iconColor={
                                    clientTheme.icons.dnrAndRevertDnrIcon
                                }
                            />
                        </IconButton>
                    </div>
                </Tooltip>
            ) : (
                <div className={`emp-dnrActionIcon-actionContainer`}>
                    {showDNRAction && (
                        <Tooltip
                            arrow
                            placement="top"
                            title={statusState.tooltip}
                        >
                            <div
                                className={` ${
                                    (!showClientTypeIssuerUI &&
                                        shouldDisableRevertDNR(
                                            row.original.properties
                                                ?.is_revert_dnr_enabled
                                        ) &&
                                        shouldDisableDNR(
                                            row.original.properties
                                                ?.is_dnr_enabled
                                        )) ||
                                    (showClientTypeIssuerUI &&
                                        row.original?.case_status?.toLocaleLowerCase() !==
                                            'rejection pending' &&
                                        selectedTabFilter !== 'not_disputed')
                                        ? 'emp-dnrActionIcon-disabled'
                                        : 'emp-dnrActionIcon-root'
                                }`}
                            >
                                <Icon
                                    onClick={
                                        isReadOnly
                                            ? () => {}
                                            : statusState.action
                                    }
                                    className={`${statusState.icon} ${
                                        isReadOnly &&
                                        'emp-dnrActionIcon-disabledIcon'
                                    }`}
                                    id={`${testId}-dnr-icon-${memoRow.id}`}
                                    iconColor={
                                        clientTheme.icons.dnrAndRevertDnrIcon
                                    }
                                />
                            </div>
                        </Tooltip>
                    )}
                    {showCaseBuilderIcon &&
                        (() => {
                            // Define allowed tab filters for each UI mode
                            const standardUIAllowedTabs = [
                                'new_cases',
                                'assigned',
                                'in_progress',
                            ]
                            const issuerUIAllowedTabs = [
                                'new_cases',
                                'assigned',
                                'expired',
                            ]

                            // Check if current tab is allowed based on UI mode
                            const isAllowedTab = !showClientTypeIssuerUI
                                ? standardUIAllowedTabs.includes(
                                      selectedTabFilter
                                  )
                                : issuerUIAllowedTabs.includes(
                                      selectedTabFilter
                                  )

                            return isAllowedTab
                        })() && (
                            <Tooltip
                                arrow
                                placement="top"
                                title={
                                    selectedTabFilter === 'in_progress'
                                        ? 'Case In Progress'
                                        : 'Case Builder'
                                }
                            >
                                {!enableCaseBuilderIcon ? (
                                    <div
                                        className={`emp-dnrActionIcon-disabled`}
                                        style={{ marginTop: '2px' }}
                                    >
                                        <Icon
                                            onClick={() => {}}
                                            className={
                                                'icon-case-builder-disabled'
                                            }
                                            id={`${testId}-case-builder-disabled-icon-${memoRow.id}`}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        className={`emp-dnrActionIcon-root`}
                                        style={{ marginTop: '2px' }}
                                    >
                                        <Icon
                                            onClick={
                                                isReadOnly
                                                    ? () => {}
                                                    : () => {
                                                          setOpenCaseBuilder(
                                                              true
                                                          )
                                                      }
                                            }
                                            className={`icon-case-builder ${
                                                isReadOnly &&
                                                'emp-dnrActionIcon-disabledIcon'
                                            }`}
                                            id={`${testId}-case-builder-icon-${memoRow.id}`}
                                        />
                                    </div>
                                )}
                            </Tooltip>
                        )}

                    {selectedTabFilter !== 'not_disputed' && (
                        <UploadIcon
                            rowIndex={memoRow.id}
                            caseIds={memoRow.original.id}
                            size="small"
                            deactivated={
                                (!showClientTypeIssuerUI &&
                                    shouldDisableUpload(
                                        memoRow.original.properties
                                            ?.is_upload_representment_enabled
                                    )) ||
                                (showClientTypeIssuerUI &&
                                    memoRow.original?.case_note?.toLocaleLowerCase() !==
                                        'missing document')
                            }
                            row={memoRow.original}
                            rehydrateView={rehydrateView}
                            testId={testId}
                            setAlertSnackbarMainProps={
                                setAlertSnackbarMainProps
                            }
                            setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                            showClientTypeIssuerUI={showClientTypeIssuerUI}
                        />
                    )}
                    {showClientTypeIssuerUI &&
                        selectedTabFilter !== 'not_disputed' && (
                            <Tooltip
                                title={'Review Document'}
                                arrow
                                placement="top"
                            >
                                <div
                                    className={`${
                                        isReadOnly ||
                                        memoRow.original?.case_status?.toLowerCase() !==
                                            'review pending'
                                            ? 'emp-dnrActionIcon-disabled'
                                            : ''
                                    }`}
                                    style={{ marginTop: '4px' }}
                                    onClick={
                                        isReadOnly ||
                                        memoRow.original?.case_status?.toLowerCase() !==
                                            'review pending'
                                            ? () => {}
                                            : () => handleClickReview()
                                    }
                                >
                                    <DocumentMagnifyingGlassIcon
                                        className="emp-dnrActionIcon-actionIcon"
                                        id={`${testId}-qcReview-document-icon-${memoRow.id}`}
                                        iconColor={
                                            isReadOnly ||
                                            memoRow.original?.case_status?.toLowerCase() !==
                                                'review pending'
                                                ? '#bdbdbd'
                                                : clientTheme.icons
                                                      .dnrAndRevertDnrIcon
                                        }
                                    />
                                </div>
                            </Tooltip>
                        )}

                    {showArchiveIcon && (
                        <Tooltip arrow placement="top" title={'Delete'}>
                            {!enableArchiveIcon ? (
                                <div className={`emp-disabled`}>
                                    <Icon
                                        onClick={() => {}}
                                        className={'icon-archive-disabled'}
                                    />
                                </div>
                            ) : (
                                <div className={`emp-root`}>
                                    <Icon
                                        onClick={
                                            isReadOnly
                                                ? () => {}
                                                : toggleArchiveDraft
                                        }
                                        className={`icon-archive ${
                                            isReadOnly && 'emp-disabledIcon'
                                        }`}
                                    />
                                </div>
                            )}
                        </Tooltip>
                    )}
                </div>
            )}
            {openMoveToDnr && (
                <DoNotRepresent
                    openModal={openMoveToDnr}
                    toggleModal={toggleMoveToDnr}
                    id={memoRow.original.id}
                    rehydrateView={rehydrateView}
                    callAction={setCaseStatus}
                    loading={casesLoading}
                    setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                    setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                    showClientTypeIssuerUI={showClientTypeIssuerUI}
                />
            )}

            {openRevertDnr && (
                <RevertDnr
                    openModal={openRevertDnr}
                    toggleModal={toggleRevertDnr}
                    id={memoRow.original.id}
                    rehydrateView={rehydrateView}
                    callAction={setCaseStatus}
                    loading={casesLoading}
                    setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                    setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                    showClientTypeIssuerUI={showClientTypeIssuerUI}
                />
            )}
            {openArchiveDraft && (
                <ArchiveDraftModal
                    openModal={openArchiveDraft}
                    toggleModal={toggleArchiveDraft}
                    id={memoRow.original.id}
                    rehydrateView={rehydrateView}
                    callAction={setCaseStatus}
                    loading={casesLoading}
                    setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                    setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                    showClientTypeIssuerUI={showClientTypeIssuerUI}
                />
            )}
            {showClientTypeIssuerUI ? (
                <CaseBuilderIssuer
                    openCaseBuilderIssuer={openCaseBuilder}
                    onClose={() => setOpenCaseBuilder(false)}
                    testId={'caseManagement'}
                />
            ) : (
                <CaseBuilder
                    openCaseBuilder={openCaseBuilder}
                    onClose={() => setOpenCaseBuilder(false)}
                    caseInfo={memoRow.original}
                    setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                    setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                    refreshGrid={rehydrateView}
                />
            )}
        </div>
    )
}

// Split apart this component if the file starts to exceeds 300 lines or pieces are reused in 3 places

export default DnrActionIcon
